import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    IconCalculator,
    IconGripVertical,
    IconInfoCircle,
    IconPlus,
    IconRefresh,
    IconTrash
} from '@tabler/icons-react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    ActionIcon,
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Group,
    Modal,
    NumberInput,
    Paper,
    SegmentedControl,
    Stack,
    Table,
    Text,
    TextInput
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { FirefinchUserStoryDto } from '../../../../api/models';
import { useGetApiProductiveServiceNames } from '../../../../api/productive/productive';

export interface CostEstimateData {
    services: string[];
    phases: string[];
    // Values may be undefined while the user is typing (empty/intermediate
    // input). Missing/undefined values default to 0 on DOCX export.
    estimates: Record<string, Record<string, number | undefined>>; // service -> phase -> estimate
    phaseDurations: Record<string, number | undefined>; // phase -> duration in weeks
    mvpFilter?: 'all' | 'mvp' | 'non-mvp';
    complicatingFactors?: {
        medicalDeviceClassAB?: boolean;
        medicalDeviceClassC?: boolean;
        techLeadChanging?: boolean;
        uncertainTechnologies?: boolean;
        pmActingAsPo?: boolean;
        complicatedStakeholders?: boolean;
    };
    multipliers?: {
        projectManagement: number;
        softwareDevelopment: number;
    };
}

interface CostEstimateFormProps {
    data?: CostEstimateData;
    onChange: (data: CostEstimateData) => void;
    userStories?: FirefinchUserStoryDto[];
    availableServices?: string[]; // Fresh services from Productive API
    hideManualSave?: boolean; // Hide the save button when auto-save is enabled
    onDelete?: () => void;
    onRefreshData?: () => Promise<void>; // Function to refresh user stories and services
}

const defaultServices = [
    'Software Development',
    'Business Analysis',
    'Project Management',
    'UI/UX Design',
    'Quality Assurance'
];

const defaultPhases = [
    'Discovery',
    'Planning',
    'Development',
    'Testing',
    'Deployment'
];

// Sortable Phase Header Component
interface SortablePhaseHeaderProps {
    phase: string;
    onRemove: (phase: string) => void;
}

const SortablePhaseHeader = ({ phase, onRemove }: SortablePhaseHeaderProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: `phase-${phase}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <Table.Th
            key={phase}
            style={{ ...style, minWidth: 120 }}
            ref={setNodeRef}
        >
            <Group justify='space-between' gap='xs'>
                <Group gap='xs'>
                    <ActionIcon
                        size='xs'
                        variant='subtle'
                        style={{ cursor: 'grab' }}
                        {...attributes}
                        {...listeners}
                    >
                        <IconGripVertical size={12} />
                    </ActionIcon>
                    <Text fw={600} size='sm'>
                        {phase}
                    </Text>
                </Group>
                <ActionIcon
                    size='xs'
                    variant='subtle'
                    color='red'
                    onClick={() => onRemove(phase)}
                >
                    <IconTrash size={12} />
                </ActionIcon>
            </Group>
        </Table.Th>
    );
};

// Sortable Service Row Component
interface SortableServiceRowProps {
    service: string;
    phases: string[];
    estimates: Record<string, Record<string, number | undefined>>;
    totals: { serviceTotals: Record<string, number> };
    onRemove: (service: string) => void;
    onUpdateEstimate: (
        service: string,
        phase: string,
        value: number | string
    ) => void;
}

const SortableServiceRow = ({
    service,
    phases,
    estimates,
    totals,
    onRemove,
    onUpdateEstimate
}: SortableServiceRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: `service-${service}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <Table.Tr key={service} style={style} ref={setNodeRef}>
            <Table.Td style={{ width: 240, minWidth: 240 }}>
                <Group justify='space-between' gap='xs' wrap='nowrap'>
                    <Group gap='xs' style={{ flex: 1, minWidth: 0 }}>
                        <ActionIcon
                            size='xs'
                            variant='subtle'
                            style={{ cursor: 'grab' }}
                            {...attributes}
                            {...listeners}
                        >
                            <IconGripVertical size={12} />
                        </ActionIcon>
                        <Text
                            size='sm'
                            fw={500}
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                flex: 1,
                                minWidth: 0
                            }}
                            title={service}
                        >
                            {service}
                        </Text>
                    </Group>
                    <ActionIcon
                        size='xs'
                        variant='subtle'
                        color='red'
                        onClick={() => onRemove(service)}
                        style={{ flexShrink: 0 }}
                    >
                        <IconTrash size={12} />
                    </ActionIcon>
                </Group>
            </Table.Td>
            {phases.map(phase => (
                <Table.Td key={`${service}-${phase}`}>
                    <NumberInput
                        size='xs'
                        min={0}
                        step={0.5}
                        decimalScale={1}
                        clampBehavior='blur'
                        value={estimates[service]?.[phase] ?? ''}
                        onChange={value =>
                            onUpdateEstimate(service, phase, value)
                        }
                        suffix=' people'
                        styles={{
                            input: {
                                textAlign: 'center',
                                fontSize: '0.875rem'
                            }
                        }}
                    />
                </Table.Td>
            ))}
            <Table.Td>
                <Text size='sm' fw={600} ta='center'>
                    {totals.serviceTotals[service]?.toFixed(1) || '0.0'}
                </Text>
            </Table.Td>
        </Table.Tr>
    );
};

export const CostEstimateForm = ({
    data,
    onChange,
    userStories,
    availableServices,
    onDelete,
    onRefreshData
}: CostEstimateFormProps) => {
    const [mvpFilter, setMvpFilter] = useState<'all' | 'mvp' | 'non-mvp'>(
        data?.mvpFilter ?? 'all'
    );
    const filteredUserStories = useMemo(() => {
        if (!userStories) return [];
        if (mvpFilter === 'mvp') return userStories.filter(s => s.mvp);
        if (mvpFilter === 'non-mvp') return userStories.filter(s => !s.mvp);
        return userStories;
    }, [userStories, mvpFilter]);

    const [services, setServices] = useState<string[]>(
        data?.services ?? defaultServices
    );
    const [phases, setPhases] = useState<string[]>(
        data?.phases ?? defaultPhases
    );
    const [estimates, setEstimates] = useState<
        Record<string, Record<string, number | undefined>>
    >(data?.estimates ?? {});
    const [phaseDurations, setPhaseDurations] = useState<
        Record<string, number | undefined>
    >(data?.phaseDurations ?? {});
    const [complicatingFactors, setComplicatingFactors] = useState(
        data?.complicatingFactors ?? {
            medicalDeviceClassAB: false,
            medicalDeviceClassC: false,
            techLeadChanging: false,
            uncertainTechnologies: false,
            pmActingAsPo: false,
            complicatedStakeholders: false
        }
    );
    const [multipliers, setMultipliers] = useState(
        data?.multipliers ?? {
            projectManagement: 1.0,
            softwareDevelopment: 1.0
        }
    );

    // Tracks the last payload we emitted via onChange (as JSON). When that
    // write round-trips back as a new `data` prop, the sync effect below would
    // otherwise reset local state — reverting edits made during the debounce
    // window and resurrecting just-deleted rows. Skipping our own echo prevents
    // the editor "fighting" the user.
    const lastEmittedRef = useRef<string | null>(null);

    // Synchronize local state with incoming data prop to prevent render loops
    useEffect(() => {
        if (data) {
            // Ignore the echo of our own most recent write — local state already
            // reflects it (and may have newer, not-yet-persisted edits).
            if (JSON.stringify(data) === lastEmittedRef.current) return;

            setServices(data.services || defaultServices);
            setPhases(data.phases || defaultPhases);
            setEstimates(data.estimates || {});
            setPhaseDurations(data.phaseDurations || {});
            setMvpFilter(data.mvpFilter ?? 'all');
            setComplicatingFactors(
                data.complicatingFactors ?? {
                    medicalDeviceClassAB: false,
                    medicalDeviceClassC: false,
                    techLeadChanging: false,
                    uncertainTechnologies: false,
                    pmActingAsPo: false,
                    complicatedStakeholders: false
                }
            );
            setMultipliers(
                data.multipliers ?? {
                    projectManagement: 1.0,
                    softwareDevelopment: 1.0
                }
            );
            setIsDirty(false);
        }
    }, [data]);

    const [addServiceOpened, { open: openAddService, close: closeAddService }] =
        useDisclosure(false);
    const [addPhaseOpened, { open: openAddPhase, close: closeAddPhase }] =
        useDisclosure(false);
    const [newServiceName, setNewServiceName] = useState('');
    const [newPhaseName, setNewPhaseName] = useState('');

    // Dirty state and save management
    const [isDirty, setIsDirty] = useState(false);

    // Get Productive services for dropdown
    const { data: productiveServices, isLoading: isLoadingServices } =
        useGetApiProductiveServiceNames();
    const productiveServiceNames =
        productiveServices?.map(service => service.name) ?? [];

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    // Update multipliers based on complicating factors
    useEffect(() => {
        // PM multiplier is additive with baseline 0.2
        let pmMultiplier = 0.2;
        let devMultiplier = 1.0;

        if (complicatingFactors.medicalDeviceClassAB) {
            pmMultiplier += 0.2; // Add 20% PM time
            devMultiplier *= 1.15; // Add 15% dev time
        }
        if (complicatingFactors.medicalDeviceClassC) {
            pmMultiplier += 0.5; // Add 50% PM time
            devMultiplier *= 1.3; // Add 30% dev time
        }
        if (complicatingFactors.techLeadChanging) {
            pmMultiplier += 0.3; // Add 30% PM time
            devMultiplier *= 1.1; // Add 10% dev time
        }
        if (complicatingFactors.uncertainTechnologies) {
            pmMultiplier += 0.4; // Add 40% PM time
            devMultiplier *= 1.25; // Add 25% dev time
        }
        if (complicatingFactors.pmActingAsPo) {
            pmMultiplier += 0.3; // Add 30% PM time
        }
        if (complicatingFactors.complicatedStakeholders) {
            pmMultiplier += 0.2; // Add 20% PM time
        }

        // Only set isDirty if the multipliers actually changed
        setMultipliers(prev => {
            if (
                prev.projectManagement !== pmMultiplier ||
                prev.softwareDevelopment !== devMultiplier
            ) {
                setIsDirty(true);
            }
            return {
                projectManagement: pmMultiplier,
                softwareDevelopment: devMultiplier
            };
        });
    }, [complicatingFactors]);

    // Regenerate phases from user stories and services from Productive
    const handleRegenerate = useCallback(async () => {
        if (!userStories || userStories.length === 0) return;

        // Fetch fresh data from APIs if callback is provided
        if (onRefreshData) {
            try {
                await onRefreshData();
            } catch (error) {
                console.error('Failed to refresh data from APIs:', error);
                // Continue with cached data if refresh fails
            }
        }

        // Extract unique phases from user stories
        const uniquePhases = Array.from(
            new Set(
                userStories
                    .filter(story => story.phase?.name)
                    .map(story => story.phase!.name!)
            )
        ).sort();

        // Use fresh services from Productive API, fall back to current productive services, then defaults
        const freshServices =
            availableServices && availableServices.length > 0
                ? availableServices
                : productiveServiceNames.length > 0
                  ? productiveServiceNames
                  : defaultServices;

        // Update both phases and services
        if (uniquePhases.length > 0) {
            setPhases(uniquePhases);
        }
        setServices(freshServices);

        setIsDirty(true);
    }, [userStories, availableServices, productiveServiceNames, onRefreshData]);

    // Auto-initialize when component loads with no data and user stories are available
    useEffect(() => {
        // Only auto-initialize if we have empty services and user stories are available
        if (
            services.length === 0 &&
            userStories &&
            userStories.length > 0 &&
            !data?.services?.length
        ) {
            void handleRegenerate();
        }
    }, [
        userStories,
        services.length,
        data?.services?.length,
        handleRegenerate
    ]);

    // Auto-save effect - triggers onChange whenever data changes
    useEffect(() => {
        if (isDirty) {
            const timeoutId = setTimeout(() => {
                const payload = {
                    services,
                    phases,
                    estimates,
                    phaseDurations,
                    mvpFilter,
                    complicatingFactors,
                    multipliers
                };
                lastEmittedRef.current = JSON.stringify(payload);
                onChange(payload);
                setIsDirty(false);
            }, 500); // 500ms debounce

            return () => clearTimeout(timeoutId);
        }
    }, [
        isDirty,
        services,
        phases,
        estimates,
        phaseDurations,
        mvpFilter,
        complicatingFactors,
        multipliers,
        onChange
    ]);

    // Initialize estimates and phase durations for new services/phases
    useEffect(() => {
        const newEstimates = { ...estimates };
        const newPhaseDurations = { ...phaseDurations };
        let changed = false;

        // Only seed entries that don't exist yet — use a key-existence check,
        // not truthiness, so a deliberately-empty (undefined) or zero value the
        // user typed isn't clobbered back to a default.
        services.forEach(service => {
            if (!newEstimates[service]) {
                newEstimates[service] = {};
                changed = true;
            }
            phases.forEach(phase => {
                if (!(phase in newEstimates[service])) {
                    newEstimates[service][phase] = 0;
                    changed = true;
                }
            });
        });

        phases.forEach(phase => {
            if (!(phase in newPhaseDurations)) {
                newPhaseDurations[phase] = 1; // Default to 1 week
                changed = true;
            }
        });

        if (changed) {
            setEstimates(newEstimates);
            setPhaseDurations(newPhaseDurations);
        }
    }, [services, phases]);

    // Allow an empty/intermediate state (undefined) so the field can be cleared
    // and typed into freely without snapping back to a valid number. Empty
    // values default to 0 on DOCX export. Min constraints are enforced on blur
    // (clampBehavior='blur'), not while typing.
    const toNumberOrUndefined = (value: number | string): number | undefined => {
        if (value === '' || value === null || value === undefined) return undefined;
        const parsed = typeof value === 'string' ? parseFloat(value) : value;
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const updateEstimate = (
        service: string,
        phase: string,
        value: number | string
    ) => {
        const numValue = toNumberOrUndefined(value);
        setEstimates(prev => ({
            ...prev,
            [service]: {
                ...prev[service],
                [phase]: numValue
            }
        }));
        setIsDirty(true);
    };

    const updatePhaseDuration = (phase: string, value: number | string) => {
        setPhaseDurations(prev => ({
            ...prev,
            [phase]: toNumberOrUndefined(value)
        }));
        setIsDirty(true);
    };

    const updateComplicatingFactor = (
        factor: keyof typeof complicatingFactors,
        checked: boolean
    ) => {
        setComplicatingFactors(prev => ({ ...prev, [factor]: checked }));
        setIsDirty(true);
    };

    const addService = () => {
        if (
            newServiceName.trim() &&
            !services.includes(newServiceName.trim())
        ) {
            setServices(prev => [...prev, newServiceName.trim()]);
            setNewServiceName('');
            closeAddService();
            setIsDirty(true);
        }
    };

    const handleCloseAddService = () => {
        setNewServiceName('');
        closeAddService();
    };

    const addPhase = () => {
        if (newPhaseName.trim() && !phases.includes(newPhaseName.trim())) {
            setPhases(prev => [...prev, newPhaseName.trim()]);
            setNewPhaseName('');
            closeAddPhase();
            setIsDirty(true);
        }
    };

    const removeService = (serviceToRemove: string) => {
        setServices(prev => prev.filter(s => s !== serviceToRemove));
        setEstimates(prev => {
            const newEstimates = { ...prev };
            delete newEstimates[serviceToRemove];
            return newEstimates;
        });
        setIsDirty(true);
    };

    const removePhase = (phaseToRemove: string) => {
        setPhases(prev => prev.filter(p => p !== phaseToRemove));
        setEstimates(prev => {
            const newEstimates = { ...prev };
            Object.keys(newEstimates).forEach(service => {
                if (newEstimates[service][phaseToRemove]) {
                    delete newEstimates[service][phaseToRemove];
                }
            });
            return newEstimates;
        });
        setPhaseDurations(prev => {
            const newPhaseDurations = { ...prev };
            delete newPhaseDurations[phaseToRemove];
            return newPhaseDurations;
        });
        setIsDirty(true);
    };

    // Drag and drop handlers
    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (!over || active.id === over.id) {
                return;
            }

            const activeId = active.id as string;
            const overId = over.id as string;

            // Handle service reordering
            if (
                activeId.startsWith('service-') &&
                overId.startsWith('service-')
            ) {
                const activeService = activeId.replace('service-', '');
                const overService = overId.replace('service-', '');

                const oldIndex = services.indexOf(activeService);
                const newIndex = services.indexOf(overService);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newServices = [...services];
                    newServices.splice(oldIndex, 1);
                    newServices.splice(newIndex, 0, activeService);
                    setServices(newServices);
                    setIsDirty(true);
                }
            }

            // Handle phase reordering
            if (activeId.startsWith('phase-') && overId.startsWith('phase-')) {
                const activePhase = activeId.replace('phase-', '');
                const overPhase = overId.replace('phase-', '');

                const oldIndex = phases.indexOf(activePhase);
                const newIndex = phases.indexOf(overPhase);

                if (oldIndex !== -1 && newIndex !== -1) {
                    const newPhases = [...phases];
                    newPhases.splice(oldIndex, 1);
                    newPhases.splice(newIndex, 0, activePhase);
                    setPhases(newPhases);
                    setIsDirty(true);
                }
            }
        },
        [services, phases]
    );

    // Calculate totals with memoization
    const totals = useMemo(() => {
        const serviceTotals: Record<string, number> = {};
        const phaseTotals: Record<string, number> = {};
        let grandTotal = 0;

        services.forEach(service => {
            serviceTotals[service] = 0;
            phases.forEach(phase => {
                const estimate = estimates[service]?.[phase] || 0;
                const duration = phaseDurations[phase] || 1;
                const totalDays = estimate * duration * 5; // Convert person-weeks to working days
                serviceTotals[service] += totalDays;
                phaseTotals[phase] = (phaseTotals[phase] || 0) + totalDays;
                grandTotal += totalDays;
            });
        });

        return { serviceTotals, phaseTotals, grandTotal };
    }, [services, phases, estimates, phaseDurations]);

    return (
        <Paper withBorder p='md' my='sm'>
            <Group justify='space-between' mb='md'>
                <Group>
                    <IconCalculator size={20} />
                    <Text fw={600} size='lg'>
                        Cost Estimate
                    </Text>
                </Group>
                <Group>
                    {userStories && userStories.length > 0 && (
                        <SegmentedControl
                            size='xs'
                            value={mvpFilter}
                            onChange={value => {
                                setMvpFilter(
                                    value as 'all' | 'mvp' | 'non-mvp'
                                );
                                setIsDirty(true);
                            }}
                            data={[
                                { label: 'All', value: 'all' },
                                { label: 'MVP', value: 'mvp' },
                                { label: 'Non-MVP', value: 'non-mvp' }
                            ]}
                        />
                    )}
                    {onDelete && (
                        <Button
                            onClick={onDelete}
                            color='red'
                            variant='subtle'
                            size='xs'
                        >
                            Delete
                        </Button>
                    )}
                </Group>
            </Group>

            {/* Instructions - Only show if user stories exist */}
            {userStories && userStories.length > 0 && (
                <Alert icon={<IconInfoCircle size={16} />} color='blue' mb='md'>
                    <Text size='xs' c='dimmed' mb='xs'>
                        Effort data and subphases are sourced from the linked
                        Notion project page. Service rates are visible in the
                        exported document and come from Productive.
                    </Text>
                    <Text size='sm' mb='xs'>
                        <strong>Instructions:</strong>
                    </Text>
                    <Text size='sm'>
                        1. First, consider the{' '}
                        <strong>complicating factors</strong> below to set
                        appropriate project complexity multipliers
                        <br />
                        2. Then populate the <strong>service table</strong> with
                        your time estimates per subphase. This is manual as
                        resource needs change per subphase.
                        <br />
                        3. Use the <strong>resource analysis</strong> on the
                        right to verify that you have increased the assigned
                        resource enough to account for project complexities
                        <br />
                        4. If you change the Notion subphases, consider using the{' '}
                        <Button
                            size='xs'
                            variant='subtle'
                            leftSection={<IconRefresh size={12} />}
                            onClick={handleRegenerate}
                            disabled={!userStories || userStories.length === 0}
                            style={{
                                display: 'inline-flex',
                                padding: '2px 6px',
                                height: 'auto',
                                fontSize: '0.75rem'
                            }}
                        >
                            REGENERATE TABLE FROM NOTION & PRODUCTIVE
                        </Button>{' '}
                        button to recreate the table. If you don&apos;t want to
                        use subphases, delete the Subphase column from the
                        Notion user stories table.
                    </Text>
                </Alert>
            )}

            {/* Complicating Factors and Resource Analysis Side by Side */}
            <Group align='flex-start' gap='md' mt='md'>
                {/* Complicating Factors - Only show if user stories exist */}
                {userStories && userStories.length > 0 && (
                    <Paper p='md' withBorder style={{ flex: 1 }}>
                        <Text fw={600} size='sm' mb='md'>
                            Complicating Factors
                        </Text>
                        <Stack gap='xs'>
                            <Checkbox
                                label='Medical device of class A/B'
                                description='Adds +0.2 PM resource required per week, ×1.15 dev multiplier'
                                checked={
                                    complicatingFactors.medicalDeviceClassAB
                                }
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'medicalDeviceClassAB',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                            <Checkbox
                                label='Medical device of class C'
                                description='Adds +0.5 PM resource required per week, ×1.30 dev multiplier'
                                checked={
                                    complicatingFactors.medicalDeviceClassC
                                }
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'medicalDeviceClassC',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                            <Checkbox
                                label='Tech lead will be changing before implementation'
                                description='Adds +0.3 PM resource required per week, ×1.10 dev multiplier'
                                checked={complicatingFactors.techLeadChanging}
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'techLeadChanging',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                            <Checkbox
                                label='Project has technologies with a lot of uncertainty'
                                description='Adds +0.4 PM resource required per week, ×1.25 dev multiplier'
                                checked={
                                    complicatingFactors.uncertainTechnologies
                                }
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'uncertainTechnologies',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                            <Checkbox
                                label='PM is acting as PO for the client'
                                description='Adds +0.3 PM resource required per week'
                                checked={complicatingFactors.pmActingAsPo}
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'pmActingAsPo',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                            <Checkbox
                                label='Client has complicated stakeholder setup'
                                description='Adds +0.2 PM resource required per week'
                                checked={
                                    complicatingFactors.complicatedStakeholders
                                }
                                onChange={event =>
                                    updateComplicatingFactor(
                                        'complicatedStakeholders',
                                        event.currentTarget.checked
                                    )
                                }
                            />
                        </Stack>
                        <Group mt='md'>
                            <Text size='xs' c='dimmed'>
                                <strong>Current factors:</strong> PM resource
                                required per week{' '}
                                {multipliers.projectManagement.toFixed(2)}, Dev
                                ×{multipliers.softwareDevelopment.toFixed(2)}
                            </Text>
                        </Group>
                    </Paper>
                )}

                {/* Resource Analysis */}
                {userStories && userStories.length > 0 && (
                    <Paper p='md' withBorder style={{ flex: 1 }}>
                        <Text fw={600} size='sm' mb='md'>
                            Resource Analysis
                        </Text>
                        <Text size='xs' c='dimmed' mb='md'>
                            Comparing estimated resourcing to effort
                            expectations
                        </Text>
                        {(() => {
                            // Check if user stories have phases that match our cost table phases
                            const userStoryPhases = filteredUserStories
                                .map(story => story.phase?.name)
                                .filter((phase): phase is string =>
                                    Boolean(phase)
                                );

                            const hasMatchingPhases =
                                userStoryPhases.length > 0 &&
                                userStoryPhases.some(userPhase =>
                                    phases.includes(userPhase)
                                );

                            if (hasMatchingPhases) {
                                // Phase-based analysis
                                const phaseAnalysis: Record<
                                    string,
                                    {
                                        expectedStoryPoints: number;
                                        devDays: number;
                                        pmDays: number;
                                        storyCount: number;
                                    }
                                > = {};

                                // Initialize phase analysis for cost table phases
                                phases.forEach(phase => {
                                    phaseAnalysis[phase] = {
                                        expectedStoryPoints: 0,
                                        devDays: 0,
                                        pmDays: 0,
                                        storyCount: 0
                                    };
                                });

                                // Add categories for stories that don't match cost table phases
                                phaseAnalysis['No Subphase'] = {
                                    expectedStoryPoints: 0,
                                    devDays: 0,
                                    pmDays: 0,
                                    storyCount: 0
                                };

                                phaseAnalysis['Subphase Mismatch'] = {
                                    expectedStoryPoints: 0,
                                    devDays: 0,
                                    pmDays: 0,
                                    storyCount: 0
                                };

                                // Track unique mismatched phases
                                const mismatchedPhases = new Set<string>();

                                // Calculate per phase from user stories
                                filteredUserStories.forEach(story => {
                                    const phaseName = story.phase?.name;
                                    const effortMin = story.effortMin ?? 0;
                                    const effortMax = story.effortMax ?? 0;
                                    const effort =
                                        effortMin && effortMax
                                            ? (effortMin + effortMax) / 2
                                            : effortMin || effortMax || 0;

                                    if (!phaseName) {
                                        // Story has no subphase
                                        phaseAnalysis[
                                            'No Subphase'
                                        ].expectedStoryPoints += effort;
                                        phaseAnalysis['No Subphase'].storyCount +=
                                            1;
                                    } else if (phases.includes(phaseName)) {
                                        // Story has a phase that matches cost table
                                        phaseAnalysis[
                                            phaseName
                                        ].expectedStoryPoints += effort;
                                        phaseAnalysis[phaseName].storyCount +=
                                            1;
                                    } else {
                                        // Story has a subphase that doesn't match cost table
                                        phaseAnalysis[
                                            'Subphase Mismatch'
                                        ].expectedStoryPoints += effort;
                                        phaseAnalysis[
                                            'Subphase Mismatch'
                                        ].storyCount += 1;
                                        mismatchedPhases.add(phaseName);
                                    }
                                });

                                // Store mismatched phases for display
                                const mismatchedPhasesList =
                                    Array.from(mismatchedPhases).sort();

                                // Calculate allocated resources per phase (only for cost table phases)
                                phases.forEach(phase => {
                                    const duration = phaseDurations[phase] ?? 1;
                                    // Convert person-weeks to person-days (multiply by 5 working days)
                                    // Sum all services except Project Management as dev days
                                    phaseAnalysis[phase].devDays =
                                        Object.entries(estimates)
                                            .filter(
                                                ([service]) =>
                                                    service !==
                                                    'Project Management'
                                            )
                                            .reduce(
                                                (sum, [, phases]) =>
                                                    sum +
                                                    (phases?.[phase] ?? 0),
                                                0
                                            ) *
                                        duration *
                                        5;
                                    phaseAnalysis[phase].pmDays =
                                        (estimates['Project Management']?.[
                                            phase
                                        ] ?? 0) *
                                        duration *
                                        5;
                                });

                                return (
                                    <Stack gap='sm'>
                                        <Text size='xs' fw={600} mb='xs'>
                                            Subphase Breakdown:
                                        </Text>
                                        {Object.entries(phaseAnalysis)
                                            .filter(
                                                ([, analysis]) =>
                                                    analysis.storyCount > 0
                                            )
                                            .sort(([a], [b]) => {
                                                // Sort "No Subphase" and "Subphase Mismatch" to the end
                                                if (a === 'No Subphase') return 1;
                                                if (b === 'No Subphase') return -1;
                                                if (a === 'Subphase Mismatch')
                                                    return 1;
                                                if (b === 'Subphase Mismatch')
                                                    return -1;
                                                return (
                                                    phases.indexOf(a) -
                                                    phases.indexOf(b)
                                                );
                                            })
                                            .map(([phase, analysis]) => {
                                                const expectedDevDaysWithComplexity =
                                                    analysis.expectedStoryPoints *
                                                    multipliers.softwareDevelopment;
                                                const devRatio =
                                                    expectedDevDaysWithComplexity >
                                                    0
                                                        ? analysis.devDays /
                                                          expectedDevDaysWithComplexity
                                                        : 0;
                                                const isNoPhase =
                                                    phase === 'No Subphase';
                                                const isPhaseMismatch =
                                                    phase === 'Subphase Mismatch';
                                                const phaseMismatchList =
                                                    isPhaseMismatch
                                                        ? mismatchedPhasesList ||
                                                          []
                                                        : [];

                                                return (
                                                    <div
                                                        key={phase}
                                                        style={{
                                                            padding: '8px',
                                                            backgroundColor:
                                                                isNoPhase
                                                                    ? 'light-dark(var(--mantine-color-yellow-0), var(--mantine-color-yellow-9))'
                                                                    : isPhaseMismatch
                                                                      ? 'light-dark(var(--mantine-color-orange-0), var(--mantine-color-orange-9))'
                                                                      : 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
                                                            borderRadius: '4px',
                                                            border: `1px solid ${
                                                                isNoPhase
                                                                    ? 'light-dark(var(--mantine-color-yellow-3), var(--mantine-color-yellow-6))'
                                                                    : isPhaseMismatch
                                                                      ? 'light-dark(var(--mantine-color-orange-3), var(--mantine-color-orange-6))'
                                                                      : 'light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))'
                                                            }`
                                                        }}
                                                    >
                                                        <Text
                                                            size='xs'
                                                            fw={600}
                                                            mb='xs'
                                                        >
                                                            {phase}
                                                        </Text>
                                                        {isPhaseMismatch &&
                                                            phaseMismatchList.length >
                                                                0 && (
                                                                <Text
                                                                    size='xs'
                                                                    c='orange'
                                                                    mb='xs'
                                                                >
                                                                    Notion
                                                                    subphases:{' '}
                                                                    {phaseMismatchList.join(
                                                                        ', '
                                                                    )}
                                                                </Text>
                                                            )}
                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns:
                                                                    '1fr 1fr 1fr',
                                                                gap: '8px',
                                                                fontSize:
                                                                    '0.75rem'
                                                            }}
                                                        >
                                                            <div>
                                                                <Text
                                                                    size='xs'
                                                                    c='dimmed'
                                                                >
                                                                    Items:{' '}
                                                                    {
                                                                        analysis.storyCount
                                                                    }
                                                                </Text>
                                                                <Text size='xs'>
                                                                    Effort
                                                                    (days):{' '}
                                                                    {analysis.expectedStoryPoints.toFixed(
                                                                        1
                                                                    )}
                                                                </Text>
                                                                <Text
                                                                    size='xs'
                                                                    c='blue'
                                                                >
                                                                    Adj:{' '}
                                                                    {(
                                                                        analysis.expectedStoryPoints *
                                                                        multipliers.softwareDevelopment
                                                                    ).toFixed(
                                                                        1
                                                                    )}
                                                                </Text>
                                                            </div>
                                                            {isNoPhase ||
                                                            isPhaseMismatch ? (
                                                                <div>
                                                                    <Text
                                                                        size='xs'
                                                                        c='dimmed'
                                                                    >
                                                                        No cost
                                                                        allocation
                                                                    </Text>
                                                                    <Text
                                                                        size='xs'
                                                                        c={
                                                                            isPhaseMismatch
                                                                                ? 'orange'
                                                                                : 'yellow'
                                                                        }
                                                                    >
                                                                        {isPhaseMismatch
                                                                            ? 'Not in cost table'
                                                                            : 'No subphase assigned'}
                                                                    </Text>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <Text
                                                                        size='xs'
                                                                        c='dimmed'
                                                                    >
                                                                        Dev
                                                                        Days:{' '}
                                                                        {analysis.devDays.toFixed(
                                                                            1
                                                                        )}
                                                                    </Text>
                                                                    <Text
                                                                        size='xs'
                                                                        c={
                                                                            devRatio >=
                                                                            1
                                                                                ? 'green'
                                                                                : 'red'
                                                                        }
                                                                    >
                                                                        {(
                                                                            devRatio *
                                                                            100
                                                                        ).toFixed(
                                                                            0
                                                                        )}
                                                                        % of
                                                                        required
                                                                    </Text>
                                                                </div>
                                                            )}
                                                            {isNoPhase ||
                                                            isPhaseMismatch ? (
                                                                <div>
                                                                    <Text
                                                                        size='xs'
                                                                        c='dimmed'
                                                                    >
                                                                        {isPhaseMismatch
                                                                            ? 'Add subphases to cost table'
                                                                            : 'Consider assigning subphase'}
                                                                    </Text>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <Text
                                                                        size='xs'
                                                                        c='dimmed'
                                                                    >
                                                                        PM
                                                                        Resource
                                                                        per
                                                                        Week:
                                                                    </Text>
                                                                    {(() => {
                                                                        // Convert PM days back to PM days per week (analysis.pmDays is now in total person-days)
                                                                        const pmPerWeek =
                                                                            (phaseDurations[
                                                                                phase
                                                                            ] ||
                                                                                1) >
                                                                            0
                                                                                ? analysis.pmDays /
                                                                                  5 /
                                                                                  (phaseDurations[
                                                                                      phase
                                                                                  ] ||
                                                                                      1)
                                                                                : 0;
                                                                        const isAdequate =
                                                                            pmPerWeek >=
                                                                            multipliers.projectManagement;
                                                                        return (
                                                                            <Text
                                                                                size='xs'
                                                                                fw={
                                                                                    600
                                                                                }
                                                                                c={
                                                                                    isAdequate
                                                                                        ? 'green'
                                                                                        : 'red'
                                                                                }
                                                                            >
                                                                                {pmPerWeek.toFixed(
                                                                                    2
                                                                                )}{' '}
                                                                                (
                                                                                {pmPerWeek -
                                                                                    multipliers.projectManagement >=
                                                                                0
                                                                                    ? '+'
                                                                                    : ''}
                                                                                {(
                                                                                    pmPerWeek -
                                                                                    multipliers.projectManagement
                                                                                ).toFixed(
                                                                                    2
                                                                                )}
                                                                                )
                                                                            </Text>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        {/* Total summary */}
                                        {(() => {
                                            const totalOriginalSP =
                                                Object.values(
                                                    phaseAnalysis
                                                ).reduce(
                                                    (sum, analysis) =>
                                                        sum +
                                                        analysis.expectedStoryPoints,
                                                    0
                                                );
                                            const totalAdjustedSP =
                                                totalOriginalSP *
                                                multipliers.softwareDevelopment;

                                            return (
                                                <div
                                                    style={{
                                                        padding: '8px',
                                                        backgroundColor:
                                                            'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))',
                                                        borderRadius: '4px',
                                                        border: '1px solid light-dark(var(--mantine-color-blue-3), var(--mantine-color-dark-4))',
                                                        marginTop: '8px'
                                                    }}
                                                >
                                                    <Text
                                                        size='xs'
                                                        fw={600}
                                                        mb='xs'
                                                    >
                                                        Total Effort (days)
                                                    </Text>
                                                    <Group gap='md' mb='xs'>
                                                        <div>
                                                            <Text
                                                                size='xs'
                                                                c='dimmed'
                                                            >
                                                                Original:
                                                            </Text>
                                                            <Text
                                                                size='sm'
                                                                fw={600}
                                                            >
                                                                {totalOriginalSP.toFixed(
                                                                    1
                                                                )}
                                                            </Text>
                                                        </div>
                                                        <div>
                                                            <Text
                                                                size='xs'
                                                                c='dimmed'
                                                            >
                                                                Adjusted (×
                                                                {multipliers.softwareDevelopment.toFixed(
                                                                    2
                                                                )}
                                                                ):
                                                            </Text>
                                                            <Text
                                                                size='sm'
                                                                fw={600}
                                                                c='blue'
                                                            >
                                                                {totalAdjustedSP.toFixed(
                                                                    1
                                                                )}
                                                            </Text>
                                                        </div>
                                                    </Group>
                                                    <Text size='xs' c='dimmed'>
                                                        Subphase-based analysis
                                                        from{' '}
                                                        {filteredUserStories.length}{' '}
                                                        effort items from Notion
                                                    </Text>
                                                </div>
                                            );
                                        })()}
                                    </Stack>
                                );
                            } else {
                                // Original total analysis when phases don't match or aren't available
                                let totalExpectedStoryPoints = 0;
                                filteredUserStories.forEach(story => {
                                    const effortMin = story.effortMin ?? 0;
                                    const effortMax = story.effortMax ?? 0;
                                    const effort =
                                        effortMin && effortMax
                                            ? (effortMin + effortMax) / 2
                                            : effortMin || effortMax || 0;
                                    totalExpectedStoryPoints += effort;
                                });

                                // Calculate total allocated days
                                let totalDevDays = 0;
                                let totalPmDays = 0;
                                phases.forEach(phase => {
                                    const duration = phaseDurations[phase] ?? 1;
                                    // Convert person-weeks to person-days (multiply by 5 working days)
                                    // Sum all services except Project Management as dev days
                                    totalDevDays +=
                                        Object.entries(estimates)
                                            .filter(
                                                ([service]) =>
                                                    service !==
                                                    'Project Management'
                                            )
                                            .reduce(
                                                (sum, [, phases]) =>
                                                    sum +
                                                    (phases?.[phase] ?? 0),
                                                0
                                            ) *
                                        duration *
                                        5;
                                    totalPmDays +=
                                        (estimates['Project Management']?.[
                                            phase
                                        ] || 0) *
                                        duration *
                                        5;
                                });

                                // Calculate totals and ratios
                                const expectedDevDaysWithComplexity =
                                    totalExpectedStoryPoints *
                                    multipliers.softwareDevelopment;
                                const devRatio =
                                    expectedDevDaysWithComplexity > 0
                                        ? totalDevDays /
                                          expectedDevDaysWithComplexity
                                        : 0;

                                const totalWeeks = Object.values(
                                    phaseDurations
                                ).reduce<number>(
                                    (sum, weeks) => sum + (weeks ?? 0),
                                    0
                                );
                                // Convert total PM days back to PM days per week (divide by 5 working days, then by total weeks)
                                const pmPerWeek =
                                    totalWeeks > 0
                                        ? totalPmDays / 5 / totalWeeks
                                        : 0;
                                const isAdequate =
                                    pmPerWeek >= multipliers.projectManagement;

                                return (
                                    <Stack gap='sm'>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: '16px',
                                                alignItems: 'start'
                                            }}
                                        >
                                            <div>
                                                <Text size='xs' c='dimmed'>
                                                    Expected Effort (days):
                                                </Text>
                                                <Text fw={600}>
                                                    {totalExpectedStoryPoints.toFixed(
                                                        1
                                                    )}
                                                </Text>
                                                <Text size='xs' c='blue'>
                                                    Adjusted:{' '}
                                                    {(
                                                        totalExpectedStoryPoints *
                                                        multipliers.softwareDevelopment
                                                    ).toFixed(1)}
                                                </Text>
                                            </div>
                                            <div>
                                                <Text size='xs' c='dimmed'>
                                                    Dev Allocation:
                                                </Text>
                                                <Text
                                                    fw={600}
                                                    c={
                                                        devRatio >= 1
                                                            ? 'green'
                                                            : 'red'
                                                    }
                                                >
                                                    {(devRatio * 100).toFixed(
                                                        0
                                                    )}
                                                    % of required
                                                </Text>
                                            </div>
                                            <div
                                                style={{ gridColumn: 'span 2' }}
                                            >
                                                <Text size='xs' c='dimmed'>
                                                    PM Resource per Week:
                                                </Text>
                                                <Text
                                                    fw={600}
                                                    c={
                                                        isAdequate
                                                            ? 'green'
                                                            : 'red'
                                                    }
                                                >
                                                    {pmPerWeek.toFixed(2)} (
                                                    {pmPerWeek -
                                                        multipliers.projectManagement >=
                                                    0
                                                        ? '+'
                                                        : ''}
                                                    {(
                                                        pmPerWeek -
                                                        multipliers.projectManagement
                                                    ).toFixed(2)}
                                                    )
                                                </Text>
                                            </div>
                                        </div>
                                        <Alert
                                            icon={<IconInfoCircle size={16} />}
                                            color='blue'
                                        >
                                            <Text size='xs'>
                                                Project total from{' '}
                                                {filteredUserStories.length} effort
                                                items from Notion (subphases
                                                don&apos;t match cost table)
                                            </Text>
                                        </Alert>
                                    </Stack>
                                );
                            }
                        })()}
                    </Paper>
                )}
            </Group>

            <Paper p='md' withBorder mt='md'>
                <Group justify='space-between' mb='md'>
                    <Text fw={600} size='sm'>
                        Estimate number of people required per subphase
                    </Text>
                    {userStories && userStories.length > 0 && (
                        <Button
                            size='xs'
                            variant='light'
                            leftSection={<IconRefresh size={12} />}
                            onClick={handleRegenerate}
                        >
                            REGENERATE TABLE FROM NOTION & PRODUCTIVE
                        </Button>
                    )}
                </Group>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <div style={{ overflowX: 'auto' }}>
                        <Table striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th
                                        style={{ minWidth: 240, width: 240 }}
                                    >
                                        <Group justify='space-between'>
                                            <Text fw={600}>Service</Text>
                                            <ActionIcon
                                                size='sm'
                                                variant='subtle'
                                                onClick={openAddService}
                                            >
                                                <IconPlus size={14} />
                                            </ActionIcon>
                                        </Group>
                                    </Table.Th>
                                    <SortableContext
                                        items={phases.map(p => `phase-${p}`)}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        {phases.map(phase => (
                                            <SortablePhaseHeader
                                                key={phase}
                                                phase={phase}
                                                onRemove={removePhase}
                                            />
                                        ))}
                                    </SortableContext>
                                    <Table.Th style={{ minWidth: 150 }}>
                                        <Group justify='space-between'>
                                            <Text fw={600}>Total (days)</Text>
                                            <ActionIcon
                                                size='sm'
                                                variant='subtle'
                                                onClick={openAddPhase}
                                            >
                                                <IconPlus size={14} />
                                            </ActionIcon>
                                        </Group>
                                    </Table.Th>
                                </Table.Tr>
                                {/* Phase durations row */}
                                <Table.Tr
                                    style={{
                                        backgroundColor:
                                            'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-6))'
                                    }}
                                >
                                    <Table.Td
                                        style={{ width: 240, minWidth: 240 }}
                                    >
                                        <Text size='sm' fw={600}>
                                            Effort Weeks (!= calendar weeks)
                                        </Text>
                                    </Table.Td>
                                    {phases.map(phase => (
                                        <Table.Td key={`weeks-${phase}`}>
                                            <NumberInput
                                                size='xs'
                                                min={0.1}
                                                step={0.5}
                                                decimalScale={1}
                                                clampBehavior='blur'
                                                value={
                                                    phaseDurations[phase] ?? ''
                                                }
                                                onChange={value =>
                                                    updatePhaseDuration(
                                                        phase,
                                                        value
                                                    )
                                                }
                                                suffix=' weeks'
                                                styles={{
                                                    input: {
                                                        textAlign: 'center',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600
                                                    }
                                                }}
                                            />
                                        </Table.Td>
                                    ))}
                                    <Table.Td></Table.Td>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                <SortableContext
                                    items={services.map(s => `service-${s}`)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {services.map(service => (
                                        <SortableServiceRow
                                            key={service}
                                            service={service}
                                            phases={phases}
                                            estimates={estimates}
                                            totals={totals}
                                            onRemove={removeService}
                                            onUpdateEstimate={updateEstimate}
                                        />
                                    ))}
                                </SortableContext>
                                <Table.Tr
                                    style={{
                                        backgroundColor:
                                            'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))'
                                    }}
                                >
                                    <Table.Td
                                        style={{ width: 240, minWidth: 240 }}
                                    >
                                        <Text size='sm' fw={700}>
                                            Subphase Total (days)
                                        </Text>
                                    </Table.Td>
                                    {phases.map(phase => (
                                        <Table.Td key={`total-${phase}`}>
                                            <Text
                                                size='sm'
                                                fw={600}
                                                ta='center'
                                            >
                                                {totals.phaseTotals[
                                                    phase
                                                ]?.toFixed(1) || '0.0'}
                                            </Text>
                                        </Table.Td>
                                    ))}
                                    <Table.Td>
                                        <Text
                                            size='sm'
                                            fw={700}
                                            ta='center'
                                            c='blue'
                                        >
                                            {totals.grandTotal.toFixed(1)}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            </Table.Tbody>
                        </Table>
                    </div>
                </DndContext>
            </Paper>

            {/* Add Service Modal */}
            <Modal
                opened={addServiceOpened}
                onClose={handleCloseAddService}
                title='Add New Service'
                size='sm'
            >
                <Autocomplete
                    label='Service Name'
                    placeholder='Select from list or enter custom service'
                    data={productiveServiceNames}
                    value={newServiceName}
                    onChange={setNewServiceName}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addService();
                        }
                    }}
                    disabled={isLoadingServices}
                    mb='md'
                />
                <Group justify='flex-end'>
                    <Button variant='subtle' onClick={handleCloseAddService}>
                        Cancel
                    </Button>
                    <Button
                        onClick={addService}
                        disabled={!newServiceName.trim()}
                    >
                        Add Service
                    </Button>
                </Group>
            </Modal>

            {/* Add Phase Modal */}
            <Modal
                opened={addPhaseOpened}
                onClose={() => {
                    setNewPhaseName('');
                    closeAddPhase();
                }}
                title='Add New Subphase'
                size='sm'
            >
                <TextInput
                    label='Subphase Name'
                    placeholder='Enter subphase name'
                    value={newPhaseName}
                    onChange={event =>
                        setNewPhaseName(event.currentTarget.value)
                    }
                    mb='md'
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addPhase();
                        }
                    }}
                />
                <Group justify='flex-end'>
                    <Button
                        variant='subtle'
                        onClick={() => {
                            setNewPhaseName('');
                            closeAddPhase();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={addPhase} disabled={!newPhaseName.trim()}>
                        Add Subphase
                    </Button>
                </Group>
            </Modal>
        </Paper>
    );
};
